import { useAuth0Client } from '@/composables/useAuth0Client';
import { isServer } from '@/helpers/Platform/Platform';
import { getLoginCallbackUrl } from '@/helpers/Route/routeHelper';
import { AppState } from '@/store/types';
import { Auth0Plugin, Auth0VueClient, createAuth0 } from '@auth0/auth0-vue';
import { watch } from 'vue';
import { Store } from 'vuex';

type Auth0UserSyncClient = Pick<Auth0Plugin, 'isAuthenticated' | 'user'>;
type Auth0ProfileSyncClient = Pick<
  Auth0Plugin,
  'isAuthenticated' | 'user' | 'getAccessTokenSilently'
>;

let auth0Instance: Auth0Plugin | undefined;

export function createAuth0Instance(): Auth0Plugin | undefined {
  auth0Instance ??= isServer()
    ? undefined
    : createAuth0(
        {
          domain: import.meta.env.VUE_APP_DOMAIN,
          clientId: import.meta.env.VUE_APP_CLIENT_ID,
          authorizationParams: {
            audience: import.meta.env.VUE_APP_AUDIENCE,
            redirect_uri: getLoginCallbackUrl(),
          },
          useRefreshTokens: true,
          useRefreshTokensFallback: true,
          sessionCheckExpiryDays: 365, // Maximum session lifetime
        },
        { skipRedirectCallback: true },
      );

  return auth0Instance;
}

export const syncLoggedInUserToStore = (
  store: Store<AppState>,
  auth0: Auth0UserSyncClient | null | undefined,
): void => {
  if (!auth0?.isAuthenticated.value) {
    store.commit('common/resetUser');
    return;
  }

  const cognitoUsername = auth0.user.value?.['cognito:username'];
  if (cognitoUsername) {
    store.commit('common/setLoggedInUser', cognitoUsername);
  }
};

export const refreshAuthProfileAndSyncStore = async (
  store: Store<AppState>,
  auth0: Auth0ProfileSyncClient | null | undefined,
): Promise<void> => {
  if (!auth0?.isAuthenticated.value) {
    store.commit('common/resetUser');
    return;
  }

  try {
    await auth0.getAccessTokenSilently();
  } catch {
    // Profile may still be available from the existing session.
  }

  syncLoggedInUserToStore(store, auth0);
};

export const getClientLoginUserId = (
  store: Store<AppState>,
  auth0: Auth0UserSyncClient | null | undefined,
): string | undefined => {
  const username = store.getters['common/getUsername'] as string;
  if (username) {
    return username;
  }

  return auth0?.user.value?.['cognito:username'] as string | undefined;
};

const waitForAuth0Ready = async (auth0Client: Auth0VueClient): Promise<void> => {
  if (!auth0Client.isLoading?.value) {
    return;
  }

  await new Promise<void>((resolve) => {
    const stop = watch(
      () => auth0Client.isLoading?.value,
      (loading) => {
        if (!loading) {
          stop();
          resolve();
        }
      },
    );
  });
};

export const checkUserAuthenticated = async (): Promise<boolean> => {
  const auth0Client = useAuth0Client();
  if (!auth0Client) {
    return false;
  }

  await waitForAuth0Ready(auth0Client);

  if (import.meta.env.VUE_APP_STAGE === 'prod' || import.meta.env.VUE_APP_STAGE === 'test') {
    await auth0Client.checkSession();
    return auth0Client.isAuthenticated.value;
  }

  try {
    return !!(await auth0Client.getAccessTokenSilently());
  } catch {
    return false;
  }
};
